import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, orgchart } from '@cwos/api-client';
import { Button, ConfirmAction, ConsequenceNote, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, gateSentence } from '../authorityGate.js';
import { writeRefusalMessage } from '../ceilingRefusal.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { pickerState } from '../pickerState.js';
import { personDirectory } from '../personDirectory.js';
import { PersonChip } from '../PersonChip.js';
import { formatWhen } from '../orgchart/coverageWindows.js';

/**
 * SUPERUSERS — A TIER, NOT A PERMISSION, AND THE SCREEN HAS TO SAY SO.
 *
 * ===========================================================================
 * WHY THESE ROUTES TAKE `orgchart:read` AND NOT SOMETHING STRONGER
 * ===========================================================================
 * `POST /orgchart/superusers` and `DELETE /orgchart/superusers/:id` are gated
 * on `orgchart:read` — a READ key on two of the most consequential writes in
 * the product — and that is deliberate rather than an oversight. The domain's
 * own comment is explicit:
 *
 *   > THE SUPERUSER ROUTES TAKE NO PERMISSION, and that is the point rather
 *   > than an omission. If designation were gated on a key, `role:assign` could
 *   > hand that key out and the tier would be reachable from the permission
 *   > system after all.
 *
 * The real gate is `evaluatePrivilegeCeiling`'s TIER RULE inside the service,
 * which reads `org_superuser` and nothing else: only a superuser may confer the
 * tier, and nobody may confer it on themselves. `orgchart:read` is there so an
 * unauthorised caller is refused before the service learns who the superusers
 * are.
 *
 * The consequence for this screen is unusual and has to be stated on it: a
 * reader holding `orgchart:read` can SEE these controls and will be refused by
 * the server with `superuser_tier_not_administrable` unless they hold the tier
 * themselves. That refusal is correct, and the screen must not present it as a
 * missing permission — "ask an administrator for a permission" is exactly the
 * wrong advice here, because no permission exists that grants it. So the
 * consequence note says the rule up front, and the refusal renderer
 * (`ceilingRefusal.ts`) names the tier rule rather than a key.
 *
 * The client cannot ask whether the reader holds the tier: `/auth/me` carries
 * permissions and roles, not `org_superuser`. Rather than guess, the screen
 * offers the control and lets the server answer — which is the same shape as
 * every other refusal in this product, and strictly better than hiding a
 * control from somebody who may in fact hold the tier.
 */
export function Superusers() {
  const permissions = usePermissions();
  const qc = useQueryClient();

  const readGate = authorityGate(permissions, 'orgchart:read');
  const mayRead = gateOpen(readGate);
  const readNote = gateSentence(readGate, 'see who holds the superuser tier');

  const canReadDirectory = mayListWorkers(permissions);
  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const people = personDirectory(
    { data: workers.data, isLoading: workers.isLoading, isError: workers.isError },
    canReadDirectory,
  );
  const candidates = (workers.data ?? []).filter((w) => w.userId);

  const tier = useQuery({
    queryKey: ['orgchart', 'superusers'],
    queryFn: () => orgchart.superusers(),
    enabled: mayRead,
  });

  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const designate = useMutation({
    mutationFn: () =>
      orgchart.designateSuperuser({
        userId,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      }),
    onSuccess: () => {
      setError(null);
      setUserId('');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['orgchart', 'superusers'] });
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t designate that person.')),
  });

  // PH-UX-004. This was a one-click `revoke.mutate(s.userId)` on a
  // `variant="destructive"` Button — the highest privilege tier in the product,
  // removed with the same effort as dismissing a toast. Row-scoped so the
  // dialog can name WHICH designation is going.
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: (targetUserId: string) => orgchart.revokeSuperuser(targetUserId),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['orgchart', 'superusers'] });
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t revoke that designation.')),
  });

  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const picker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Choose someone…',
  );

  // `mayRead` false and RESOLVED is the only reading entitled to the refusal
  // phase; unresolved and unreadable are `loading` and `failed` respectively,
  // which is what `listPhase` produces from the query itself.
  const phase =
    readGate.kind === 'refused'
      ? ({ kind: 'refused' } as const)
      : listPhase(tier, tier.data?.superusers.length);

  return (
    <section className="rn-card rn-col" aria-label="Superusers">
      <h2 className="rn-card-title">Superusers</h2>
      <p className="rn-muted">
        A superuser is a tier, not a permission. It is deliberately not reachable from the
        permission system — no key confers it, so no role and no direct grant can hand it out — and
        only somebody who already holds it may confer it on somebody else. Nobody may confer it on
        themselves.
      </p>

      {readNote ? (
        <p className="rn-muted" role="status" data-superuser-gate={readGate.kind}>
          {readNote}
        </p>
      ) : null}

      <ListState
        phase={phase}
        surface="table"
        rows={3}
        failedMessage="We couldn’t load the superuser tier just now. Do not read this as “this organization has no superusers”."
        onRetry={() => void tier.refetch()}
        empty={
          <SuccessEmpty
            heading="Nobody holds the tier"
            message="The server was asked and returned no live designations. This organization has no superuser, which also means nobody can currently designate one — that is a real state and it needs platform support to leave."
          />
        }
      >
        <>
          <p data-superuser-count={tier.data?.superusers.length ?? 0}>
            {tier.data
              ? `${tier.data.superusers.length} of a maximum ${tier.data.maxSuperusers} designations are live.`
              : null}
          </p>
          <ul className="rn-list">
            {(tier.data?.superusers ?? []).map((s) => (
              <li key={s.id} data-superuser={s.userId}>
                <PersonChip
                  person={people.person(s.userId)}
                  detail={`designated ${formatWhen(s.designatedAt)}`}
                />
                {s.designatedBy ? (
                  <span className="rn-muted"> · by {people.name(s.designatedBy)}</span>
                ) : (
                  <span className="rn-muted">
                    {' '}
                    · no designating actor recorded. That is the seed designation, not a missing
                    name.
                  </span>
                )}
                <Button variant="destructive" small onClick={() => setConfirmRevoke(s.userId)}>
                  Revoke this designation
                </Button>
              </li>
            ))}
          </ul>
          {/*
           * PH-UX-004. The consequence that has to be on screen is not "this
           * is permanent" — it is the TIER RULE in the docblock above: only a
           * superuser may confer the tier. So revoking the last live
           * designation is a one-way door for the whole organization, and the
           * dialog says so in as many words when that is what is about to
           * happen. The count comes from the same `tier.data` the list renders,
           * so the warning cannot disagree with the list above it.
           */}
          <ConfirmAction
            open={confirmRevoke !== null}
            title={
              confirmRevoke !== null
                ? `Revoke ${people.name(confirmRevoke)}’s superuser designation?`
                : 'Revoke this superuser designation?'
            }
            consequence={
              (confirmRevoke !== null
                ? `This removes the superuser tier from ${people.name(confirmRevoke)} immediately. ` +
                  `They keep their roles and permissions; what they lose is the tier — including ` +
                  `the ability to designate or revoke other superusers.`
                : '') +
              ((tier.data?.superusers.length ?? 0) <= 1
                ? ' This is the LAST live designation. Only a superuser can confer the tier, so ' +
                  'once it is gone nobody in this organization can designate a new one — ' +
                  'restoring it needs platform support. There is no way to undo this from here.'
                : ' Only a superuser can confer the tier, so it can be restored only by one of ' +
                  'the remaining superusers.')
            }
            confirmLabel="Revoke designation"
            pendingLabel="Revoking…"
            pending={revoke.isPending}
            onCancel={() => setConfirmRevoke(null)}
            onConfirm={() => {
              if (confirmRevoke !== null) revoke.mutate(confirmRevoke);
            }}
          />
        </>
      </ListState>

      <h3>Designate somebody</h3>
      <div className="rn-row">
        <label>
          Person
          <select
            value={userId}
            disabled={picker.disabled}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">{picker.placeholder}</option>
            {candidates.map((w) => (
              <option key={w.userId!} value={w.userId!}>
                {w.displayName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Why
          <input
            type="text"
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
      </div>
      {picker.note ? (
        <p className="rn-muted" role="status">
          {picker.note}
        </p>
      ) : null}

      <ConsequenceNote text="This confers the highest tier in the product, permanently until revoked. Only a superuser may do it, and you may not do it to yourself — if the server refuses, that is the tier rule and not a missing permission. No permission exists that would let you do this, so asking an administrator for more access will not help." />

      <Button
        variant="primary"
        disabled={userId === '' || designate.isPending}
        onClick={() => designate.mutate()}
      >
        {designate.isPending ? 'Designating…' : 'Designate as superuser'}
      </Button>

      {error ? (
        <p role="alert" data-superuser-error="true">
          {error}
        </p>
      ) : null}
    </section>
  );
}
